import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import useMutation from "../../../../../hooks/useMutation";
import { UPDATE_INTERNAL_WORK_ORDER_MUTATION } from "../../../../../constants/graphql/mutations";
import AssetQuantityField from "../../../../../views/inventory/requests/AssetQuantityField";

const styles = {
  dialog: {
    "& .MuiDialog-paper": { width: "500px", maxWidth: "100%" },
  },
  closeButton: {
    position: "absolute",
    right: 8,
    top: 8,
    color: "text.secondary",
  },
  subtitle: {
    mb: 4,
  },
};

const ITEMIZED_FIELDS = [
  { key: "installer_rates", label: "Installer Hours" },
  { key: "warehouse_labor_rates", label: "Warehouse Labor Hours" },
  { key: "fork_lift_rates", label: "Forklift Hours" },
];

function getInitialBlendedValue(billingQuantities) {
  if (!billingQuantities) return 0;
  if (Array.isArray(billingQuantities) && billingQuantities.length > 0) {
    return billingQuantities[0].quantity_count || 0;
  }
  return billingQuantities.time_and_materials || 0;
}

function getInitialItemizedValues(billingQuantities) {
  const defaults = { installer_rates: 0, warehouse_labor_rates: 0, fork_lift_rates: 0 };
  if (!billingQuantities) return defaults;
  if (Array.isArray(billingQuantities)) {
    billingQuantities.forEach(entry => {
      const type = entry.quantity_type || entry.quantityType;
      if (type && type in defaults) {
        defaults[type] = entry.quantity_count || entry.quantityCount || 0;
      }
    });
  }
  return defaults;
}

export default function InternalBillingQuantitiesModal({
  internalWorkOrder,
  onClose,
  onSave,
}) {
  const isItemized = internalWorkOrder?.billingType === "Time and Materials (Itemized Rates)";

  const [billableHours, setBillableHours] = useState(() =>
    getInitialBlendedValue(internalWorkOrder.billingQuantities)
  );
  const [itemizedHours, setItemizedHours] = useState(() =>
    getInitialItemizedValues(internalWorkOrder.billingQuantities)
  );

  const [updateInternalWorkOrder] = useMutation(UPDATE_INTERNAL_WORK_ORDER_MUTATION);

  const projectSettings = internalWorkOrder?.project?.settings || {};
  const timeIncrementMinutes = parseInt(projectSettings.time_increment_minutes || 60, 10);

  let step = 1;
  let inputReadOnly = false;
  let label = "# of Hours";

  if (timeIncrementMinutes !== 60) {
     if (timeIncrementMinutes === 15) step = 0.25;
     else if (timeIncrementMinutes === 20) step = 0.33;
     else if (timeIncrementMinutes === 30) step = 0.5;

     inputReadOnly = true;
     label = `# of Hours (per ${timeIncrementMinutes} mins)`;
  }

  const isValid = isItemized
    ? ITEMIZED_FIELDS.some(({ key }) => itemizedHours[key] > 0)
    : billableHours > 0;

  const handleSave = async () => {
    let billingQuantities;

    if (isItemized) {
      billingQuantities = ITEMIZED_FIELDS.map(({ key }) => ({
        quantity_type: key,
        quantity_count: Number(itemizedHours[key]),
      }));
    } else {
      billingQuantities = [
        {
          quantity_type: "time_and_materials",
          quantity_count: Number(billableHours),
        },
      ];
    }

    await updateInternalWorkOrder({
      variables: {
        id: internalWorkOrder.id,
        input: { billingQuantities },
      },
    });

    onSave();
  };

  const handleCloseAttempt = () => {
    if (isValid) {
      onClose();
    }
  };

  const handleItemizedChange = (key, value) => {
    setItemizedHours(prev => ({ ...prev, [key]: value }));
  };

  return (
    <Dialog
      open={true}
      onClose={handleCloseAttempt}
      disableEscapeKeyDown={!isValid}
      sx={styles.dialog}
    >
      <DialogContent>
        {isValid && (
          <IconButton
            onClick={handleCloseAttempt}
            sx={styles.closeButton}
            title="Close"
          >
            <CloseIcon />
          </IconButton>
        )}
        <Box>
          <Typography
            fontSize={22}
            fontWeight={500}
            color="text.primary"
            align="center"
            sx={{ mb: 2 }}
          >
            Internal Order Billable Quantities
          </Typography>
          <Typography fontSize={15} color="text.secondary" align="center" sx={styles.subtitle}>
            {isItemized
              ? "Enter billable hours by labor type for this internal work order"
              : "Enter the total billable hours for this internal work order"}
          </Typography>

          {isItemized ? (
            <Box mt={4} mb={6} display="flex" flexDirection="column" gap={3}>
              {ITEMIZED_FIELDS.map(({ key, label: fieldLabel }) => (
                <Box key={key} display="flex" alignItems="center" gap={2}>
                  <Box
                    width="50%"
                    sx={{
                      border: "1px solid #ccc",
                      borderRadius: 1,
                      p: 1.5,
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <Typography fontSize={14} color="text.primary">
                      {fieldLabel}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" width="50%">
                    <AssetQuantityField
                      size="medium"
                      quantity={itemizedHours[key]}
                      onChange={quantity => handleItemizedChange(key, quantity)}
                      step={step}
                      inputReadOnly={inputReadOnly}
                      quantityProps={{
                        label: label,
                        style: { minWidth: 120 },
                        InputLabelProps: {
                          shrink: true,
                        },
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box display="flex" alignItems="center" gap={2} mt={4} mb={6}>
              <Box
                width="50%"
                sx={{
                  border: "1px solid #ccc",
                  borderRadius: 1,
                  p: 1.5,
                  backgroundColor: "#f5f5f5",
                }}
              >
                <Typography fontSize={14} color="text.primary">
                  Billable Hours
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" width="50%">
                <AssetQuantityField
                  size="medium"
                  quantity={billableHours}
                  onChange={quantity => setBillableHours(quantity)}
                  step={step}
                  inputReadOnly={inputReadOnly}
                  quantityProps={{
                    label: label,
                    style: { minWidth: 120 },
                    InputLabelProps: {
                      shrink: true,
                    },
                  }}
                />
              </Box>
            </Box>
          )}

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button variant="outlined" onClick={onClose}>
              Back
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={!isValid}>
              Save
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
